import React, { useState, useEffect, useCallback } from "react";
import apiClient from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ClipboardList, Save, ChevronLeft, ChevronRight, Lock, Clock, Users, PlusCircle } from "lucide-react";

const MONTH_NAMES = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

const PrestationsPage = () => {
  const { user } = useAuth();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [form, setForm] = useState({ shifts: 0, cours_collectifs: 0, heures_supplementaires: 0 });
  const [editable, setEditable] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [history, setHistory] = useState([]);

  const isCurrentMonth = year === today.getFullYear() && month === today.getMonth() + 1;

  const fetchPrestation = useCallback(async () => {
    try {
      const res = await apiClient.get(`/prestations/me?year=${year}&month=${month}`);
      const p = res.data.prestation;
      setEditable(res.data.editable);
      if (p) {
        setForm({ shifts: p.shifts, cours_collectifs: p.cours_collectifs, heures_supplementaires: p.heures_supplementaires });
      } else {
        setForm({ shifts: 0, cours_collectifs: 0, heures_supplementaires: 0 });
      }
    } catch (err) {
      console.error(err);
    }
  }, [year, month]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await apiClient.get("/prestations/me/history");
      setHistory(res.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { fetchPrestation(); }, [fetchPrestation]);
  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await apiClient.post("/prestations/", form);
      setSaved(true);
      fetchHistory();
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (isCurrentMonth) return;
    if (month === 12) { setMonth(1); setYear(year + 1); }
    else setMonth(month + 1);
  };

  const totalHeures = (form.shifts * 4) + (form.cours_collectifs * 2) + form.heures_supplementaires;

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto" data-testid="prestations-page">
      <div className="flex items-center gap-3 mb-6">
        <ClipboardList size={24} className="text-[#3899ec]" />
        <div>
          <h1 className="text-xl font-bold text-[#162d3d]">Mes Prestations</h1>
          <p className="text-sm text-[#577083]">{user?.name || user?.email}</p>
        </div>
      </div>

      {/* Month selector */}
      <div className="flex items-center justify-center gap-4 mb-6">
        <button onClick={prevMonth} className="p-2 rounded-lg hover:bg-[#edf0f4] transition-colors" data-testid="prev-month">
          <ChevronLeft size={20} className="text-[#577083]" />
        </button>
        <span className="text-lg font-semibold text-[#162d3d] min-w-[180px] text-center" data-testid="current-period">
          {MONTH_NAMES[month - 1]} {year}
        </span>
        <button onClick={nextMonth} disabled={isCurrentMonth} className={`p-2 rounded-lg transition-colors ${isCurrentMonth ? 'opacity-30 cursor-not-allowed' : 'hover:bg-[#edf0f4]'}`} data-testid="next-month">
          <ChevronRight size={20} className="text-[#577083]" />
        </button>
      </div>

      {!editable && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-center gap-2 text-sm text-amber-700" data-testid="locked-message">
          <Lock size={16} />
          Ce mois est verrouillé. Vous ne pouvez plus modifier vos prestations.
        </div>
      )}

      {/* Form */}
      <div className="bg-white rounded-xl border border-[#edf0f4] shadow-sm p-5 mb-6">
        <div className="grid gap-5">
          {/* Shifts */}
          <div>
            <label className="text-sm font-medium text-[#162d3d] mb-1.5 flex items-center gap-2">
              <Clock size={16} className="text-[#3899ec]" />
              Nombre de shifts réalisés
              <span className="text-xs text-[#8fa4b5] font-normal">(1 shift = 4h)</span>
            </label>
            <Input
              type="number" min="0" value={form.shifts}
              onChange={(e) => setForm({...form, shifts: parseInt(e.target.value) || 0})}
              disabled={!editable}
              className="h-11 text-lg"
              data-testid="input-shifts"
            />
          </div>

          {/* Cours collectifs */}
          <div>
            <label className="text-sm font-medium text-[#162d3d] mb-1.5 flex items-center gap-2">
              <Users size={16} className="text-[#22c55e]" />
              Cours collectifs hors Omnia ou pendant shifts
              <span className="text-xs text-[#8fa4b5] font-normal">(1 cours = 2h)</span>
            </label>
            <Input
              type="number" min="0" value={form.cours_collectifs}
              onChange={(e) => setForm({...form, cours_collectifs: parseInt(e.target.value) || 0})}
              disabled={!editable}
              className="h-11 text-lg"
              data-testid="input-cours"
            />
          </div>

          {/* Heures sup */}
          <div>
            <label className="text-sm font-medium text-[#162d3d] mb-1.5 flex items-center gap-2">
              <PlusCircle size={16} className="text-[#f59e0b]" />
              Heures supplémentaires autorisées
            </label>
            <Input
              type="number" min="0" step="0.5" value={form.heures_supplementaires}
              onChange={(e) => setForm({...form, heures_supplementaires: parseFloat(e.target.value) || 0})}
              disabled={!editable}
              className="h-11 text-lg"
              data-testid="input-heures-sup"
            />
          </div>
        </div>

        {/* Recap */}
        <div className="mt-6 p-4 rounded-xl bg-[#f4f7fa] border border-[#edf0f4]" data-testid="recap-section">
          <h3 className="text-sm font-semibold text-[#162d3d] mb-3">Récapitulatif — {MONTH_NAMES[month - 1]} {year}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="text-center p-3 rounded-lg bg-white border border-[#edf0f4]">
              <div className="text-2xl font-bold text-[#3899ec]" data-testid="recap-shifts">{form.shifts}</div>
              <div className="text-xs text-[#577083]">Shifts</div>
            </div>
            <div className="text-center p-3 rounded-lg bg-white border border-[#edf0f4]">
              <div className="text-2xl font-bold text-[#22c55e]" data-testid="recap-cours">{form.cours_collectifs}</div>
              <div className="text-xs text-[#577083]">Cours collectifs</div>
            </div>
            <div className="text-center p-3 rounded-lg bg-white border border-[#edf0f4]">
              <div className="text-2xl font-bold text-[#f59e0b]" data-testid="recap-heures-sup">{form.heures_supplementaires}h</div>
              <div className="text-xs text-[#577083]">Heures sup.</div>
            </div>
            <div className="text-center p-3 rounded-lg bg-[#1a2744] text-white">
              <div className="text-2xl font-bold text-[#e8d44d]" data-testid="recap-total">{totalHeures}h</div>
              <div className="text-xs text-white/70">Total heures</div>
            </div>
          </div>
        </div>

        {editable && (
          <Button onClick={handleSave} disabled={saving} className="w-full mt-5 h-11 bg-[#1a2744] hover:bg-[#243459] text-[#e8d44d] font-semibold" data-testid="save-prestation">
            <Save size={16} className="mr-2" />
            {saving ? "Enregistrement..." : saved ? "Enregistré !" : "Enregistrer mes prestations"}
          </Button>
        )}
        {saved && <p className="text-sm text-green-600 text-center mt-2">Vos prestations ont été enregistrées avec succès.</p>}
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white rounded-xl border border-[#edf0f4] shadow-sm p-5">
          <h3 className="text-sm font-semibold text-[#162d3d] mb-3">Historique</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[#8fa4b5] text-xs border-b border-[#edf0f4]">
                  <th className="pb-2 pr-4">Mois</th>
                  <th className="pb-2 pr-4 text-center">Shifts</th>
                  <th className="pb-2 pr-4 text-center">Cours</th>
                  <th className="pb-2 pr-4 text-center">H. sup.</th>
                  <th className="pb-2 text-center">Total</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={i} className="border-b border-[#edf0f4] last:border-0">
                    <td className="py-2 pr-4 font-medium text-[#162d3d]">{MONTH_NAMES[h.month - 1]} {h.year}</td>
                    <td className="py-2 pr-4 text-center">{h.shifts}</td>
                    <td className="py-2 pr-4 text-center">{h.cours_collectifs}</td>
                    <td className="py-2 pr-4 text-center">{h.heures_supplementaires}h</td>
                    <td className="py-2 text-center font-semibold text-[#1a2744]">{h.total_heures}h</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrestationsPage;
